import type { RiskDecomposition } from '@/types/api'
import { parseDecimalString, formatCurrency } from '@/lib/chart-transformers'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { BarChart, Activity, PieChart, TrendingUp, Info } from 'lucide-react'

interface RiskDecompositionPanelProps {
  decomposition: RiskDecomposition
}

export function RiskDecompositionPanel({ decomposition }: RiskDecompositionPanelProps) {
  const {
    marginal_contributions,
    concentration_metrics,
    factor_exposures,
    portfolio_concentration,
  } = decomposition

  // Parse concentration metrics
  const hhi = parseDecimalString(concentration_metrics.hhi)
  const top3Concentration = parseDecimalString(concentration_metrics.top3_concentration)

  // Parse portfolio concentration
  const cryptoPct = parseDecimalString(portfolio_concentration.crypto_percentage)
  const tradfiPct = parseDecimalString(portfolio_concentration.tradfi_percentage)

  // Calculate total marginal contribution
  const totalMarginalContribution = marginal_contributions.reduce(
    (sum, mc) => sum + parseDecimalString(mc.contribution),
    0
  )

  // Top 5 contributors
  const topContributors = [...marginal_contributions]
    .sort((a, b) => parseDecimalString(b.contribution) - parseDecimalString(a.contribution))
    .slice(0, 5)

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <BarChart className="h-5 w-5 text-blue-500" />
          Risk Decomposition
        </CardTitle>
        <CardDescription>
          Factor exposures, concentration metrics, and risk contributions
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Portfolio Concentration */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <PieChart className="h-4 w-4 text-gray-500" />
            <h4 className="text-sm font-medium">Portfolio Concentration</h4>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-purple-600 font-medium">Crypto</span>
              <span className="font-mono">{cryptoPct.toFixed(1)}%</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-purple-500 rounded-full transition-all"
                style={{ width: `${Math.min(cryptoPct, 100)}%` }}
              />
            </div>

            <div className="flex justify-between text-sm">
              <span className="text-emerald-600 font-medium">TradFi</span>
              <span className="font-mono">{tradfiPct.toFixed(1)}%</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500 rounded-full transition-all"
                style={{ width: `${Math.min(tradfiPct, 100)}%` }}
              />
            </div>
          </div>
        </div>

        {/* Concentration Metrics */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-gray-500" />
            <h4 className="text-sm font-medium">Concentration Metrics</h4>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3 w-3 text-gray-400 cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs max-w-xs">
                    <strong>HHI:</strong> Herfindahl-Hirschman Index (0-10000).
                    Higher values indicate more concentration.
                    {' >'}2500 is highly concentrated.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">HHI Index</p>
              <p className={`text-lg font-mono font-medium ${
                hhi > 2500 ? 'text-red-600' : hhi > 1500 ? 'text-amber-600' : 'text-emerald-600'
              }`}>
                {hhi.toFixed(0)}
              </p>
              <p className="text-xs text-gray-400">
                {hhi > 2500 ? 'High concentration' : hhi > 1500 ? 'Moderate' : 'Diversified'}
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">Top 3 Positions</p>
              <p className="text-lg font-mono font-medium text-gray-700">
                {top3Concentration.toFixed(1)}%
              </p>
              <p className="text-xs text-gray-400">of portfolio</p>
            </div>
          </div>
        </div>

        {/* Factor Exposures */}
        {factor_exposures.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-gray-500" />
              <h4 className="text-sm font-medium">Factor Exposures</h4>
            </div>

            <div className="space-y-2">
              {factor_exposures.slice(0, 4).map((factor) => {
                const exposure = parseDecimalString(factor.exposure)
                const beta = parseDecimalString(factor.beta)

                return (
                  <div key={factor.factor} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="capitalize">{factor.factor}</span>
                      <span className="font-mono text-xs">
                        β={beta.toFixed(2)} | {exposure >= 0 ? '+' : ''}{formatCurrency(exposure)}
                      </span>
                    </div>
                    <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`absolute h-full rounded-full ${
                          exposure >= 0 ? 'bg-emerald-500' : 'bg-red-500'
                        }`}
                        style={{
                          width: `${Math.min(Math.abs(exposure) / 1000 * 100, 100)}%`,
                          left: exposure >= 0 ? '50%' : `${50 - Math.min(Math.abs(exposure) / 1000 * 100, 50)}%`,
                        }}
                      />
                      <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gray-300" />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Marginal Contributions */}
        {topContributors.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <BarChart className="h-4 w-4 text-gray-500" />
              <h4 className="text-sm font-medium">Top Risk Contributors</h4>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3 w-3 text-gray-400 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs max-w-xs">
                      Marginal contribution to portfolio risk (VaR).
                      Higher values indicate assets that contribute most to overall portfolio risk.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            <div className="space-y-2">
              {topContributors.map((contributor) => {
                const contribution = parseDecimalString(contributor.contribution)
                const percentage = totalMarginalContribution > 0
                  ? (contribution / totalMarginalContribution) * 100
                  : 0

                return (
                  <div key={contributor.assetId} className="flex items-center gap-3">
                    <span className="text-sm font-medium w-16">{contributor.symbol}</span>
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full transition-all"
                        style={{ width: `${Math.min(percentage, 100)}%` }}
                      />
                    </div>
                    <span className="text-xs font-mono text-gray-600 w-16 text-right">
                      {percentage.toFixed(1)}%
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

interface EmptyRiskDecompositionPanelProps {
  reason?: 'no-data' | 'loading'
}

export function EmptyRiskDecompositionPanel({ reason = 'no-data' }: EmptyRiskDecompositionPanelProps) {
  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <BarChart className="h-5 w-5 text-blue-500" />
          Risk Decomposition
        </CardTitle>
        <CardDescription>Factor exposures and concentration metrics</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-center justify-center py-8 bg-gray-50 rounded-lg">
          <BarChart className="h-10 w-10 text-gray-300 mb-2" />
          <p className="text-gray-500">
            {reason === 'loading' ? 'Computing risk metrics...' : 'No risk decomposition data'}
          </p>
        </div>
      </CardContent>
    </Card>
  )
}
